/**
 * useCloseCreativeAttribution.ts
 *
 * Fetches per-creative CRM attribution stats from the backend cache
 * (which aggregates data from Close CRM's Source Content field).
 *
 * Returns a map: creative label (lowercase) → { leads, applications, mqls, closed, revenue }
 */

import { useState, useEffect } from "react";

export interface CloseCreativeRow {
  creative:     string;
  leads:        number;
  applications: number;
  mqls:         number;
  closed:       number;
  revenue:      number;
}

export type CloseCreativeMap = Map<string, CloseCreativeRow>;

export interface CloseCreativeAttribution {
  data:    CloseCreativeMap;
  loading: boolean;
  error:   string | null;
}

export function useCloseCreativeAttribution(): CloseCreativeAttribution {
  const [data, setData]       = useState<CloseCreativeMap>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const resp = await fetch("/api/close-creative-attribution");
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json() as { success: boolean; data: CloseCreativeRow[] };
        if (!cancelled) {
          const map = new Map<string, CloseCreativeRow>();
          for (const row of json.data ?? []) {
            map.set(row.creative.toLowerCase().trim(), row);
          }
          setData(map);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return { data, loading, error };
}
